import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { ThumbUp, CheckCircle, ThumbDown } from '@mui/icons-material';
import MenuAnalysisChip from './MenuAnalysisChip';
import { useTranslation } from 'react-i18next';
const MenuAnalysisResults = ({
  analysis,
  showBestOrder = true,
  showAlternatives = true,
  showItemsToAvoid = true,
  compact = false,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const translateCount = (count) => {
    // First ensure we have a valid number
    const num = Number(count);
    if (isNaN(num)) return count; // Return original if not a number

    try {
      // Fallback to 'en' if language isn't set
      const language = i18n?.language || 'en';
      return new Intl.NumberFormat(language, {
        maximumFractionDigits: 0,
        useGrouping: false,
      }).format(num);
    } catch (error) {
      console.error('Number formatting error:', error);
      return num; // Return the plain number if formatting fails
    }
  };
  if (!analysis || analysis.status !== 'completed') {
    return null;
  }

  return (
    <Box>
      {/* Best Order */}
      {showBestOrder && analysis.bestOrder && (
        <Box style={{ marginBottom: compact ? '12px' : '15px' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <ThumbUp style={{ color: '#4CAF50', fontSize: compact ? '18px' : isMobile ? '20px' : '24px' }} />
            <Typography variant={isMobile ? 'body1' : 'subtitle2'} style={{ fontWeight: '600', color: '#333' }}>
              {t('bestOrder')}
            </Typography>
          </Box>
          <Box
            style={{
              padding: compact ? '8px' : isMobile ? '12px' : '16px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e8f5e8',
            }}
          >
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              style={{ color: '#26786A', fontWeight: '600', marginBottom: '4px' }}
            >
              {analysis.bestOrder.item_name}
            </Typography>
            {
              <Typography
                variant="body2"
                style={{
                  color: '#666',
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  lineHeight: '1.4',
                }}
              >
                {analysis.bestOrder.justification}
              </Typography>
            }
          </Box>
        </Box>
      )}

      {/* Alternative Recommendations */}
      {showAlternatives && analysis.alternativeRecommendations && analysis.alternativeRecommendations.length > 0 && (
        <Box style={{ marginBottom: compact ? '12px' : '15px' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <CheckCircle style={{ color: '#2196F3', fontSize: compact ? '18px' : isMobile ? '20px' : '24px' }} />
            <Typography variant={isMobile ? 'body1' : 'subtitle2'} style={{ fontWeight: '600', color: '#333' }}>
              {t('alternativeOptions')} ({analysis.alternativeRecommendations.length})
            </Typography>
          </Box>
          <Box
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: isMobile ? '6px' : '8px',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {analysis.alternativeRecommendations.map((rec, index) => (
              <MenuAnalysisChip
                key={index}
                item={rec}
                type="alternative"
                compact={compact}
                index={index}
                isMobile={isMobile}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Items to Avoid */}
      {showItemsToAvoid && analysis.itemsToAvoid && analysis.itemsToAvoid.length > 0 && (
        <Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <ThumbDown style={{ color: '#f44336', fontSize: compact ? '18px' : isMobile ? '20px' : '24px' }} />
            <Typography variant={isMobile ? 'body1' : 'subtitle2'} style={{ fontWeight: '600', color: '#333' }}>
              {t('itemsToAvoid')} ({translateCount(analysis.itemsToAvoid.length)})
            </Typography>
          </Box>
          <Box
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: isMobile ? '6px' : '8px',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {analysis.itemsToAvoid.map((item, index) => (
              <MenuAnalysisChip
                key={index}
                item={item}
                type="avoid"
                compact={compact}
                index={index}
                isMobile={isMobile}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MenuAnalysisResults;
